import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  Users,
  ClipboardList,
  MessageSquare,
  ArrowRight,
  RefreshCw,
  LogOut,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'reset_admin_token';

const CARDS = [
  {
    key: 'waitlist',
    title: '2027 Waitlist',
    subtitle: 'Brothers ready for Panama',
    href: '/admin/waitlist',
    icon: Users,
    countUrl: `${API}/reset-2027/waitlist/count`,
    countKey: 'total',
    testid: 'admin-card-waitlist',
  },
  {
    key: 'surveys',
    title: 'Wealth Survey',
    subtitle: 'Homepage & standalone survey',
    href: '/admin/surveys',
    icon: ClipboardList,
    countUrl: `${API}/survey/wealth/count`,
    countKey: 'count',
    testid: 'admin-card-surveys',
  },
  {
    key: 'feedback',
    title: 'Retreat Feedback',
    subtitle: 'Post-event responses',
    href: '/admin/feedback',
    icon: MessageSquare,
    countUrl: `${API}/feedback/retreat/count`,
    countKey: 'count',
    testid: 'admin-card-feedback',
  },
];

export default function AdminHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialToken =
    searchParams.get('token') || localStorage.getItem(TOKEN_KEY) || '';

  const [token, setToken] = useState(initialToken);
  const [authed, setAuthed] = useState(false);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const verifyAndLoad = async (t) => {
    setLoading(true);
    setError('');
    try {
      // Verify token by hitting an admin-protected endpoint
      const auth = await fetch(`${API}/reset-2027/waitlist?limit=1`, {
        headers: { 'X-Admin-Token': t },
      });
      if (auth.status === 401) {
        setError('Invalid admin token. Try again.');
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        return;
      }
      if (!auth.ok) {
        setError(`Error ${auth.status}. Please retry.`);
        return;
      }
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      if (searchParams.get('token')) {
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });
      }
      // Fetch counts (public endpoints; no token needed)
      const results = await Promise.all(
        CARDS.map(async (c) => {
          try {
            const r = await fetch(c.countUrl);
            if (!r.ok) return [c.key, null];
            const j = await r.json();
            return [c.key, j[c.countKey] ?? j.count ?? j.total ?? 0];
          } catch (_e) {
            return [c.key, null];
          }
        }),
      );
      setCounts(Object.fromEntries(results));
    } catch (_e) {
      setError('Network error. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialToken) verifyAndLoad(initialToken);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    verifyAndLoad(token.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setCounts({});
  };

  // Preserve token in outbound links so sub-dashboards don't require re-entry
  const withToken = (href) => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? `${href}?token=${encodeURIComponent(t)}` : href;
  };

  // --- Login gate ---
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6"
        data-testid="admin-login-page"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md bg-[#111111] border border-white/10 rounded-2xl p-8 md:p-10"
          data-testid="admin-login-form"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-cormorant text-3xl font-medium text-white text-center mb-2">
            Admin Home
          </h1>
          <p className="text-zinc-400 font-manrope text-sm text-center mb-6">
            Enter your admin token to access all dashboards.
          </p>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12 mb-4"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <p
              className="text-red-400 font-manrope text-sm mb-4 text-center"
              data-testid="admin-login-error"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading || !token.trim()}
            className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? 'Verifying\u2026' : 'Unlock'}
          </Button>
        </form>
      </div>
    );
  }

  // --- Dashboard ---
  const totalActivity = CARDS.reduce(
    (sum, c) => sum + (typeof counts[c.key] === 'number' ? counts[c.key] : 0),
    0,
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-home-page">
      <header className="border-b border-white/5 bg-[#0A0A0A]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center font-cormorant font-bold text-lg">
              R
            </div>
            <div>
              <p className="text-white font-cormorant text-lg leading-tight">
                Admin
              </p>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest">
                The Reset
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => verifyAndLoad(token || localStorage.getItem(TOKEN_KEY) || '')}
              disabled={loading}
              className="text-zinc-500 hover:text-white font-manrope text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
              data-testid="admin-home-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="text-zinc-500 hover:text-red-400 font-manrope text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
              data-testid="admin-home-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="mb-10">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-3">
            Command Center
          </p>
          <h1 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight mb-3">
            Every brother, <span className="italic text-[#D4AF37]">every signal.</span>
          </h1>
          <p className="text-zinc-400 font-manrope text-base max-w-2xl">
            {"Three dashboards, one door. Waitlist signups, wealth surveys, and retreat feedback \u2014 all in one place. "}
            {totalActivity > 0 && (
              <span className="text-white">Total inputs collected: <b>{totalActivity}</b>.</span>
            )}
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {CARDS.map((c) => {
            const Icon = c.icon;
            const n = counts[c.key];
            return (
              <Link
                key={c.key}
                to={withToken(c.href)}
                className="group rounded-2xl bg-[#111111] border border-white/10 hover:border-[#D4AF37]/50 p-6 transition-colors"
                data-testid={c.testid}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-colors">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <p className="font-cormorant text-5xl font-medium text-white leading-none mb-2">
                  {typeof n === 'number' ? n : '\u2014'}
                </p>
                <p className="text-white font-manrope text-base font-semibold mb-1">
                  {c.title}
                </p>
                <p className="text-zinc-500 font-manrope text-xs">
                  {c.subtitle}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Helper strip */}
        <div className="rounded-2xl bg-[#111111] border border-white/10 p-5 text-zinc-500 font-manrope text-xs leading-relaxed">
          <p>
            <span className="text-white font-semibold">Tip:</span>
            {" Bookmark this page. Your token is saved locally so you\u2019ll land here signed-in every time. Click any card to open its full dashboard \u2014 CSV export lives inside each one."}
          </p>
        </div>
      </div>
    </div>
  );
}
